import { useMemo } from "react"
import { useQueries } from "@tanstack/react-query"
import { omit } from "lodash"

// Works around https://github.com/TanStack/query/issues/8224
// by combining identical queries, then re-spreading the results.
//
// `select` is stripped from every consolidated query (including unique-key queries) so that
// TanStack caches the raw response and deduplication works correctly when two callers share a key.
// Tradeoff: for non-deduplicated queries TanStack's built-in `select` memoisation is bypassed —
// the transform runs in `combine` instead, which is called on each results change but is not
// reference-stable. In practice gatherer selects run on every data update regardless, so the
// impact is negligible.
export default function useCombinedQueries(initialQueries, initialCombine) {
  const [queries, combine] = useMemo(() => {
    const keyIndexes = {}
    const consolidatedQueries = []

    const queryMap = initialQueries.map(query => {
      if (!query) return null
      const stringKey = JSON.stringify(query.queryKey)
      if (keyIndexes[stringKey] === undefined) {
        keyIndexes[stringKey] = consolidatedQueries.length
        consolidatedQueries.push(omit(query, "select"))
      }
      return { consolidatedIndex: keyIndexes[stringKey], select: query.select }
    })

    const combine = results => initialCombine(
      queryMap.map(entry => {
        if (!entry) return null
        const result = results[entry.consolidatedIndex]
        return entry.select && result?.data != null
          ? { ...result, data: entry.select(result.data) }
          : result
      }),
    )

    return [consolidatedQueries, combine]
  }, [initialQueries, initialCombine])

  return useQueries({ queries, combine })
}
